import { ModeOfPlay } from '../models/IBattlePack.js';
import { battleplanDocumentToRuleDocument } from './ruleDocument.js';
// ─── Bundle content collections (the single source of truth) ──────────────────
// Every optional array field on a bundle — the authorable content beyond
// battlePack + battleplans — is registered here ONCE. Normalization (toBundle),
// merging (mergeBundles), and persistence (studio library) all iterate this list
// instead of hand-naming fields, so adding a new collection means adding it here
// (and to the interface above) and nothing else. The compile-time guard below
// fails the build if a new array field is added to BattlepackBundle but not
// registered, which is exactly the bug that kept silently dropping data.
export const BUNDLE_COLLECTION_KEYS = [
    'battleTactics',
    'rulesDocuments',
    'armies',
    'regimentsOfRenown',
];
const BUNDLE_MERGE_MODE = {
    battleTactics: 'replace',
    rulesDocuments: 'replace',
    armies: 'concat',
    regimentsOfRenown: 'concat',
};
const _allBundleCollectionsRegistered = true;
function isRecord(value) {
    return !!value && typeof value === 'object';
}
// Normalize any supported on-disk shape into a BattlepackBundle:
//   - a BattlepackBundle (pass-through),
//   - a legacy BattlepackMaps ({ battlePack, maps }),
//   - a legacy IBattleplanDocument ({ battlePack, title, sections }).
// Returns null if the shape is unrecognized. This is the single entry point that
// understands the old two-file formats, so callers never branch on file kind.
export function toBundle(parsed) {
    if (!isRecord(parsed) || !parsed.battlePack)
        return null;
    const battlePack = parsed.battlePack;
    // Already a bundle. Copy battlePack + battleplans, then carry every registered
    // collection that's present (generic — new collections need no edit here).
    if (isRecord(parsed.battleplans)) {
        const out = {
            battlePack,
            battleplans: parsed.battleplans,
        };
        if (typeof parsed.studioVersion === 'string')
            out.studioVersion = parsed.studioVersion;
        for (const key of BUNDLE_COLLECTION_KEYS) {
            const value = parsed[key];
            if (Array.isArray(value))
                out[key] = value;
        }
        return out;
    }
    // Legacy maps-only file.
    if (isRecord(parsed.maps)) {
        const battleplans = {};
        for (const [name, map] of Object.entries(parsed.maps)) {
            battleplans[name] = { map: map };
        }
        return { battlePack, battleplans };
    }
    // Legacy authored document.
    if (Array.isArray(parsed.sections)) {
        const battleplans = {};
        for (const section of parsed.sections) {
            for (const container of section.containers) {
                battleplans[container.battleplan.name] = { authored: container.battleplan };
            }
        }
        return { battlePack, battleplans };
    }
    return null;
}
// Combine bundles into one per battlepack. Bundles are given in increasing
// precedence — LATER bundles are the more "local"/homebrew source and win. On a
// battleplan-name collision the merge is field-level: a later bundle's map or
// authored content overrides, but a field it doesn't provide is kept from the
// earlier (e.g. an add-on bundle that only adds a map to an existing battleplan
// extends it rather than wiping its rules). Distinct names are unioned.
//
// Use this to layer official + homebrew packs before registering. The map
// registry's registerBattlepackBundle is authoritative-replace (one source of
// truth, as the editor needs), so merging happens here, before registration.
export function mergeBundles(...bundles) {
    const byPack = new Map();
    for (const bundle of bundles) {
        const existing = byPack.get(bundle.battlePack);
        if (!existing) {
            const copy = {
                battlePack: bundle.battlePack,
                battleplans: { ...bundle.battleplans },
            };
            for (const key of BUNDLE_COLLECTION_KEYS) {
                const value = bundle[key];
                if (value)
                    copy[key] = [...value];
            }
            byPack.set(bundle.battlePack, copy);
            continue;
        }
        for (const [name, entry] of Object.entries(bundle.battleplans)) {
            const prev = existing.battleplans[name];
            existing.battleplans[name] = prev
                ? { map: entry.map ?? prev.map, authored: entry.authored ?? prev.authored }
                : { ...entry };
        }
        // Each registered collection merges per its declared mode (replace/concat).
        for (const key of BUNDLE_COLLECTION_KEYS) {
            const incoming = bundle[key];
            if (!incoming)
                continue;
            if (BUNDLE_MERGE_MODE[key] === 'concat') {
                const prev = (existing[key] ?? []);
                existing[key] = [...prev, ...incoming];
            }
            else {
                existing[key] = incoming;
            }
        }
    }
    return [...byPack.values()];
}
// True when an authored battleplan carries real content. The battleplan's `name`
// is intentionally NOT considered — it's redundant with the entry key (the bundle
// is keyed by name), so an "authored" block with only a name is blank and must be
// treated as map-only. Used to decide what becomes a rules document / selectable
// battleplan, and mirrored by the editor's empty-authored guard.
export function hasAuthoredContent(authored) {
    if (!authored)
        return false;
    return (!!authored.rulesText?.trim() ||
        !!authored.twist?.trim() ||
        !!authored.scoringText?.trim() ||
        (authored.scoringRules?.length ?? 0) > 0 ||
        (authored.abilities?.length ?? 0) > 0 ||
        (authored.sections?.some((section) => !!section.title.trim() ||
            !!section.text?.trim() ||
            !!section.trailingText?.trim() ||
            (section.abilities?.length ?? 0) > 0) ??
            false));
}
// The authored battleplans in a bundle as a BattleplansMap (the structure the
// score tracker / rules browser consume). Maps-only and blank-authored entries
// contribute nothing. The IBattleplan's name is derived from the entry key, so a
// bundle whose authored blocks omit `name` (or drift) still resolves correctly.
export function bundleToBattleplansMap(bundle) {
    const inner = new Map();
    for (const [name, entry] of Object.entries(bundle.battleplans)) {
        if (hasAuthoredContent(entry.authored))
            inner.set(name, { ...entry.authored, name });
    }
    return new Map([[bundle.battlePack, inner]]);
}
// A synthetic RuleDocument for the authored battleplans in a bundle so they are
// findable/renderable in the rules browser & search. Each authored battleplan is
// its own section holding a single `missionType` container — mirroring the
// official battleplan documents (e.g. ghb25_battleplans.json), so the browser
// renders one collapsible BattleplanCard per plan rather than cramming them all
// into one section. Maps-only bundles produce a document with no sections.
export function bundleToRuleDocument(bundle, title) {
    const doc = {
        title: title ?? `${bundle.battlePack} Battleplans`,
        battlePack: bundle.battlePack,
        sections: Object.entries(bundle.battleplans)
            .filter(([, entry]) => hasAuthoredContent(entry.authored))
            .map(([name, entry]) => ({
            name,
            containers: [
                {
                    name,
                    containerType: 'missionType',
                    // Name derived from the key (see bundleToBattleplansMap).
                    battleplan: { ...entry.authored, name },
                },
            ],
        })),
    };
    return battleplanDocumentToRuleDocument(doc);
}
// The battleplan sections a rules doc embeds via a `battleplans` marker — the
// same per-plan sections bundleToRuleDocument produces (DRY).
export function battleplanRuleSections(bundle) {
    return bundleToRuleDocument(bundle).sections;
}
// One `standard` container per battle-tactic card, matching the GHB doc shape so
// RuleSection.cardFromContainer reconstructs it (leading text + Affray/Strike/
// Domination battleTactic components titled "<Type> - <name>").
function battleTacticContainers(bundle) {
    return (bundle.battleTactics ?? []).map((card, i) => {
        const components = [];
        if (card.text)
            components.push({ contentType: 'text', text: card.text });
        for (const [label, t] of [
            ['Affray', card.affray],
            ['Strike', card.strike],
            ['Domination', card.domination],
        ]) {
            components.push({
                contentType: 'battleTactic',
                title: `${label} - ${t.name}`,
                text: t.description,
            });
        }
        return {
            name: `Battle Tactics Card ${i + 1} - ${card.name}`,
            containerType: 'standard',
            components,
        };
    });
}
function sectionsFor(ref, bundle) {
    if (ref === 'battleplans')
        return battleplanRuleSections(bundle);
    const containers = battleTacticContainers(bundle);
    // BattlePack may be a CustomBattlePack; RuleSection's IBattlePack uses the
    // narrower known-enum union, so cast at the seam (mirrors ruleDocument.ts).
    const battlePack = bundle.battlePack;
    return containers.length
        ? [
            {
                name: 'Battle Tactics',
                modeOfPlay: ModeOfPlay.MatchedPlay,
                subSections: [],
                containers,
                battlePack,
            },
        ]
        : [];
}
function containersFor(ref, bundle) {
    if (ref === 'battleTactics')
        return battleTacticContainers(bundle);
    return battleplanRuleSections(bundle).flatMap((s) => s.containers);
}
function expandSection(section, bundle) {
    if (section.ref)
        return sectionsFor(section.ref, bundle);
    return [
        {
            ...section,
            subSections: section.subSections.flatMap((s) => expandSection(s, bundle)),
            containers: section.containers.flatMap((c) => (c.ref ? containersFor(c.ref, bundle) : [c])),
        },
    ];
}
// Resolve every `ref` marker in a rules document against the bundle's
// battleplans / battle tactics. Pure; returns a new document.
export function expandRuleDocument(doc, bundle) {
    return { ...doc, sections: doc.sections.flatMap((s) => expandSection(s, bundle)) };
}
